import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import { type BulkAssignResult } from 'services/member/memberStaffService'
import {
  type BaseStaffInformation,
  type ExecRole,
  transformResponseAddress,
  transformStafferRequestCommon
} from 'services'

export interface ExecutivePositionReference {
  id: number
  title: string | null
}

export interface ExecutiveStaffInformation extends BaseStaffInformation {
  execrole: ExecRole
  execrole2?: ExecRole | null
  execrole3?: ExecRole | null
  execpos: ExecutivePositionReference
}

export async function getStaffByExecutivePosition (
  positionId: number
): Promise<HttpResponse<ExecutiveStaffInformation[]>> {
  const response = await handleHttpRequest<ExecutiveStaffInformation[]>(
    http.get(`/api/v1/us/executives/positions/${positionId}/staffers/`)
  )
  if (response.result != null) {
    response.result = response.result.map(staffer => ({
      ...staffer,
      address: transformResponseAddress(staffer.address)
    }))
  }
  return response
}

export async function bulkAssignExecPositionStaffers (
  positionId: number
): Promise<HttpResponse<BulkAssignResult>> {
  return await handleHttpRequest(
    http.post(`/api/v1/us/executives/positions/${positionId}/staffers/bulk-assign/`, {})
  )
}

export function transformRequestExecStaffer (staffer: ExecutiveStaffInformation): Record<string, any> {
  return {
    ...transformStafferRequestCommon(staffer),
    execrole: staffer.execrole,
    execrole2: staffer.execrole2 ?? null,
    execrole3: staffer.execrole3 ?? null,
    execpos: staffer.execpos
  }
}

export async function updateExecutiveStaffer (
  staffer: ExecutiveStaffInformation
): Promise<HttpResponse<ExecutiveStaffInformation>> {
  const response = await http.put(
    `/api/v1/us/executives/positions/${staffer.execpos.id}/staffers/${staffer.id}/`,
    transformRequestExecStaffer(staffer)
  )

  return {
    ...response.data,
    result: {
      ...response.data.result,
      address: transformResponseAddress(response.data.result.address)
    }
  }
}

export async function createExecutiveStaffer (
  staffer: ExecutiveStaffInformation
): Promise<HttpResponse<ExecutiveStaffInformation>> {
  const response = await http.post(
    `/api/v1/us/executives/positions/${staffer.execpos.id}/staffers/`,
    transformRequestExecStaffer(staffer)
  )

  return {
    ...response.data,
    result: {
      ...response.data.result,
      address: transformResponseAddress(response.data.result.address)
    }
  }
}

export async function retireExecutiveStaffer (
  positionId: number,
  stafferId: number
): Promise<HttpResponse<void>> {
  return await handleHttpRequest(
    http.delete(`/api/v1/us/executives/positions/${positionId}/staffers/${stafferId}/`)
  )
}

export async function reorderExecutiveStaffers (
  positionId: number,
  idAndDisplayOrderMap: Record<number, number>,
  selectedId: number,
  execRoleId: number
): Promise<HttpResponse<[]>> {
  return await handleHttpRequest<[]>(
    http.patch(`/api/v1/us/executives/positions/${positionId}/staffers/`, {
      idAndDisplayOrderMap,
      selectedId,
      roleType: execRoleId
    })
  )
}
